import {
  animate,
  AnimationTriggerMetadata,
  AUTO_STYLE,
  group,
  style,
  transition,
  trigger,
} from '@angular/animations';

import { NotificationPosition } from './models/notification.enum';

/** Shape accepted by the `[@toastAnimation]` host binding. */
export interface ToastAnimationParams {
  value: string;
  params: {
    enterTransform: string;
    leaveTransform: string;
    enterDuration: string;
    leaveDuration: string;
  };
}

const ENTER_DURATION = '280ms';
const LEAVE_DURATION = '200ms';

/** Springy on the way in, quick and linear on the way out. */
const ENTER_EASING = 'cubic-bezier(0.21, 1.02, 0.73, 1)';
const LEAVE_EASING = 'cubic-bezier(0.55, 0, 0.55, 0.2)';

/**
 * Slides a toast in from the nearest screen edge while fading it up, then
 * fades it out and collapses its height so the rest of the stack closes the
 * gap in the same frame instead of snapping.
 */
export const toastAnimation: AnimationTriggerMetadata = trigger('toastAnimation', [
  transition(
    ':enter',
    [
      style({ opacity: 0, transform: '{{ enterTransform }}' }),
      animate('{{ enterDuration }} ' + ENTER_EASING, style({ opacity: 1, transform: 'none' })),
    ],
    {
      params: {
        enterTransform: 'translate3d(0, -16px, 0) scale(0.96)',
        enterDuration: ENTER_DURATION,
      },
    }
  ),
  transition(
    ':leave',
    [
      // Captured up front so the collapse has a concrete start value; `overflow`
      // is not animated, it just stops the shadow bleeding during the collapse.
      style({ overflow: 'hidden', height: AUTO_STYLE, marginBottom: AUTO_STYLE }),
      group([
        animate(
          '{{ leaveDuration }} ' + LEAVE_EASING,
          style({ opacity: 0, transform: '{{ leaveTransform }}' })
        ),
        animate('{{ leaveDuration }} ease-in', style({ height: 0, marginBottom: 0 })),
      ]),
    ],
    {
      params: {
        leaveTransform: 'translate3d(0, -8px, 0) scale(0.94)',
        leaveDuration: LEAVE_DURATION,
      },
    }
  ),
]);

/**
 * Picks the travel direction for a position: edge-anchored stacks slide in
 * horizontally, centred stacks drop down / rise up from their own edge.
 */
export function toastAnimationParams(position: NotificationPosition): ToastAnimationParams {
  const durations = { enterDuration: ENTER_DURATION, leaveDuration: LEAVE_DURATION };

  switch (position) {
    case NotificationPosition.TopLeft:
    case NotificationPosition.BottomLeft:
      return {
        value: position,
        params: {
          enterTransform: 'translate3d(-110%, 0, 0) scale(0.96)',
          leaveTransform: 'translate3d(-110%, 0, 0) scale(0.94)',
          ...durations,
        },
      };

    case NotificationPosition.TopRight:
    case NotificationPosition.BottomRight:
      return {
        value: position,
        params: {
          enterTransform: 'translate3d(110%, 0, 0) scale(0.96)',
          leaveTransform: 'translate3d(110%, 0, 0) scale(0.94)',
          ...durations,
        },
      };

    case NotificationPosition.BottomCenter:
      return {
        value: position,
        params: {
          enterTransform: 'translate3d(0, 120%, 0) scale(0.96)',
          leaveTransform: 'translate3d(0, 24px, 0) scale(0.94)',
          ...durations,
        },
      };

    case NotificationPosition.TopCenter:
    default:
      return {
        value: position,
        params: {
          enterTransform: 'translate3d(0, -120%, 0) scale(0.96)',
          leaveTransform: 'translate3d(0, -24px, 0) scale(0.94)',
          ...durations,
        },
      };
  }
}
